import Feature, { FeatureLike } from "ol/Feature"
import { selectMany } from "../../utils/array"
import { isUndefinedOrNull } from "../../utils/utils"
import { Layer } from "../layer"
import { IMapContainer } from "../types"
import { getFeaturePoints, observeOpenlayersObject } from "../utils"
import { MeasureOverlay } from "./overlay"
import { IMapMeasuringService, IMapMeasuringTool } from "./types"
import { ol_Collection, ol_events_condition, ol_Feature, ol_FeatureLike, ol_Map, ol_MapBrowserPointerEvent, ol_Overlay, ol_source, ol_style, ol_geom, ol_interaction, ol_layer } from "@omnia/exports/openlayers";
import VectorSource from "ol/source/Vector"
import { Geometry } from "ol/geom"


/** @internal */
export class MeasuringTool implements IMapMeasuringTool {

    private _olmap: ol_Map;
    private _measuringSvc: IMapMeasuringService;

    private _pointerWatcher: Function;

    private _tooltipOverlay: ol_Overlay;
    private _tooltipFeature: ol_Feature;

    private _active: boolean = false;
    private _enableSelection: boolean;
    private _enableContextMenu: boolean

    private _measuringLayer: ol_layer.Vector;
    private _highlighLayer: ol_layer.Vector;

    private _editInteraction: ol_interaction.Modify;
    private _drawInteraction: ol_interaction.Draw;
    private _snapInteraction: ol_interaction.Snap;

    private _newMeasure: boolean;
    private _currentMeasure: MeasureOverlay;
    private _overlays: MeasureOverlay[] = [];

    private _highlight: {
        feature: ol_FeatureLike
        overlay: MeasureOverlay;
    }


    constructor(private map: IMapContainer, private timeout: (fn: () => void) => void) {
        this._olmap = map.getMap();
        this._measuringSvc = map.getMeasuringService();
    }

    dispose() {

        this.releaseSnapInteraction();
        this.releaseDrawInteraction();
        this.releaseEditInteraction();
        this.releaseCurrentFeature();

        if (this._pointerWatcher) {
            this._pointerWatcher();
            this._pointerWatcher = null;
        }

        this.releaseTooltip();

        if (this._overlays) {
            this._overlays.forEach(o => o.dispose());
            this._overlays = [];
        }

        this.highlightFeature(null);

        if (this._measuringLayer) {
            this._measuringLayer.getSource().clear();
            this._olmap.removeLayer(this._measuringLayer);
            this._measuringLayer = null;
        }

        if (this._highlighLayer) {
            this._olmap.removeLayer(this._highlighLayer);
            this._highlighLayer = null;
        }

        if (!isUndefinedOrNull(this._enableSelection)) {
            this.map.enableSelection = this._enableSelection;
            this._enableSelection = null;
        }

        if (!isUndefinedOrNull(this._enableContextMenu)) {
            this.map.enableContextMenu = this._enableContextMenu;
            this._enableContextMenu = null;
        }

        this._olmap.render();


    }

    releaseTooltip() {

        if (this._tooltipOverlay) {
            this._olmap.removeOverlay(this._tooltipOverlay);
            this._tooltipOverlay = null;
        }


        if (this._tooltipFeature) {
            if (this._measuringLayer) {
                this._measuringLayer.getSource().removeFeature(this._tooltipFeature);
            }
            this._tooltipFeature = null;
        }
    }

    releaseSnapInteraction() {

        if (this._snapInteraction) {
            this._olmap.removeInteraction(this._snapInteraction);
            this._snapInteraction = null;
        }

    }

    releaseEditInteraction() {

        if (this._editInteraction) {
            this._olmap.removeInteraction(this._editInteraction);
            this._editInteraction = null;
        }
    }

    releaseDrawInteraction() {

        if (this._drawInteraction) {
            this._olmap.removeInteraction(this._drawInteraction);
            this._drawInteraction = null;
        }

    }

    private releaseCurrentFeature() {

        if (this._currentMeasure) {
            this._currentMeasure.dispose();
            this._currentMeasure = null;
        }

    }

    private setupSnapInteraction() {

        this.releaseSnapInteraction();

        let layers = this.map.getLayers().reverse();

        let features = new ol_Collection(selectMany(layers.map(l => l.getSnappingFeatures())).filter(f => !!f));

        let snap = new ol_interaction.Snap({
            features: features
        });

        this._snapInteraction = snap;
        this._olmap.addInteraction(snap);

    }

    private setupEditInteraction() {

        this.releaseEditInteraction();

        this._editInteraction = new ol_interaction.Modify({
            features: this._measuringLayer.getSource().getFeaturesCollection(),
            deleteCondition: ol_events_condition.click
        });

        this._olmap.addInteraction(this._editInteraction);

    }

    private activate() {

        this.dispose();

        this._pointerWatcher = observeOpenlayersObject(this._olmap, "pointermove", event => this.onPointerMove(event));

        this._measuringLayer = this.createLayer(this._measureStyle);

        this._highlighLayer = this.createLayer(this._highlightStyle);

        if (isUndefinedOrNull(this._enableSelection)) {
            this._enableSelection = this.map.enableSelection;
            this.map.enableSelection = false;
        }

        this.setupEditInteraction();

    }

    private createLayer(style: ol_style.Style[]) {

        let layer = new ol_layer.Vector({
            source: new ol_source.Vector({
                useSpatialIndex: false,
            }),
            updateWhileAnimating: true,
            updateWhileInteracting: true,
            style: style,
        });

        layer.setMap(this._olmap);

        return layer;

    }


    hasMeasures(): boolean {

        if (this._newMeasure || this._currentMeasure) {
            return true;
        }

        if (this._overlays && this._overlays.length) {
            return true;
        }

        return false;

    }

    clearMeasures() {
        let active = this._active;
        this.setActive(false);
        this.setActive(active);
    }

    isDrawingNewMeasure() {
        return this._drawInteraction && this._drawInteraction.getActive();
    }

    isActive() {
        return this._active
    }

    setActive(active: boolean) {

        if (!active) {
            this.dispose();
        }
        else if (!this._active) {
            this.activate();
        }

        this._active = active;

    }


    private createTooltipFeature() {

        let feature = new ol_Feature();
        this._measuringLayer.getSource().addFeature(feature);

        return feature;
    }

    private startDrawing(length: boolean) {

        const type = (length ? "LineString" : "Polygon");

        this._editInteraction.setActive(false);

        this._drawInteraction = new ol_interaction.Draw({
            type,
            style: this._measureStyle
        });

        this._drawInteraction.on("drawstart", event => this.onDrawStart(length, event));
        this._drawInteraction.on("drawend", event => this.onDrawEnd(event));
        this._drawInteraction.setActive(true);

        this._olmap.addInteraction(this._drawInteraction);

        this.setupSnapInteraction();

        this.releaseTooltip();
        this._tooltipFeature = this.createTooltipFeature();
        this._newMeasure = true;

        if (isUndefinedOrNull(this._enableContextMenu)) {
            this._enableContextMenu = this.map.enableContextMenu;
            this.map.enableContextMenu = false;
        }

    }


    private onPointerMove(event: ol_MapBrowserPointerEvent) {

        if (!event) {
            return
        }

        if (this._newMeasure) {

            if (!this._currentMeasure) {
                this.updateDuringDraw(event);
            }

            return;

        }

        this.highlightFeatureAtPointer(event);

    }

    private highlightFeatureAtPointer(event: ol_MapBrowserPointerEvent) {

        let match: ol_FeatureLike

        if ((!event.dragging) && (!this._drawInteraction)) {

            this._olmap.forEachFeatureAtPixel(event.pixel, (feature, olayer) => {

                let layer = feature.get("layer");

                if (layer instanceof Layer) {
                    match = feature;
                    return true;

                }

            });

        }

        this.highlightFeature(match);

    }

    private highlightFeature(feature: ol_FeatureLike) {

        if (!this._highlighLayer) return;

        if (this._highlight) {
            if (this._highlight.feature === feature) return;
            this._highlight.overlay.dispose();
            this._highlighLayer.getSource().clear();
            this._highlight = null;
        }

        if (feature) {
            let geom = (<any>(feature.getGeometry())).clone();
            let hlfeature = new ol_Feature(geom);
            this._highlighLayer.getSource().addFeature(hlfeature);

            let overlay = new MeasureOverlay(hlfeature, false, this._olmap, this._measuringSvc, false);

            this._highlight = {
                feature,
                overlay
            };
        }

    }

    private updateDuringDraw(event: ol_MapBrowserPointerEvent) {

        if (this._tooltipOverlay) {
            this._tooltipOverlay.setPosition(event.coordinate);
        }

        if (this._tooltipFeature) {
            let point = new ol_geom.Point(event.coordinate);
            this._tooltipFeature.setGeometry(point);
        }

    }

    private onDrawStart(length: boolean, event: { feature: ol_Feature }) {

        let feature = event.feature;

        let overlay = new MeasureOverlay(feature, length, this._olmap, this._measuringSvc, true);

        this._currentMeasure = overlay;
        this._newMeasure = false;

        this.releaseTooltip();

    }

    private onDrawEnd(event: { feature: ol_Feature }) {

        this.releaseDrawInteraction();

        this._measuringLayer.getSource().addFeature(event.feature);
        this._overlays.push(this._currentMeasure);
        this._currentMeasure = null;

        this._editInteraction.setActive(true);

        this.timeout(() => {

            if (!isUndefinedOrNull(this._enableContextMenu)) {
                this.map.enableContextMenu = this._enableContextMenu;
                this._enableContextMenu = null;
            }

        });

    }


    private startNewMeasure(length: boolean) {
        if (this._active) {
            this.startDrawing(length);
        }
    }

    startMeasuringLength() {
        this.startNewMeasure(true);
    }

    startMeasuringArea() {
        this.startNewMeasure(false);
    }

    private _imageStyle = new ol_style.Circle({

        radius: 5,

        stroke: new ol_style.Stroke({
            color: '#0679ca',
            width: 3,
        }),

        fill: new ol_style.Fill({
            color: '#ffffff'
        }),

    });


    private _measureStyle = [

        new ol_style.Style({

            fill: new ol_style.Fill({
                color: 'rgba(255, 255, 255, 0.2)'
            }),

            stroke: new ol_style.Stroke({
                color: '#0679ca',
                lineDash: [10, 10],
                width: 5
            }),

            image: this._imageStyle

        }),

        new ol_style.Style({

            stroke: new ol_style.Stroke({
                color: '#ffffff',
                lineDash: [10, 10],
                width: 1
            }),

        }),

        new ol_style.Style({

            geometry: feature => getFeaturePoints(feature, true),

            image: this._imageStyle,

        }),

    ];

    private _highlightStyle = [

        new ol_style.Style({

            fill: new ol_style.Fill({
                color: 'rgba(255, 255, 255, 0.2)'
            }),

            stroke: new ol_style.Stroke({
                color: '#0679ca',
                width: 5
            }),

            image: this._imageStyle

        }),

        new ol_style.Style({

            stroke: new ol_style.Stroke({
                color: '#ffffff',
                width: 1
            }),

        }),

    ];


}
